import type { Location, NavigateFunction } from 'react-router-dom';
import type { CaseLocationCell } from './caseLocationUtils';

/** サンプル ID を URL に載せない（Router location.state のみ） */
export const SAMPLE_DETAIL_PATH = '/samples/detail';
export const SAMPLE_USAGE_HISTORY_PATH = '/samples/usage-history';

/** タブ内リロード用。URL／履歴には残さない */
const SAMPLE_DETAIL_SESSION_KEY = 'kensuke.sampleDetail.v1';
const SAMPLE_USAGE_HISTORY_SESSION_KEY = 'kensuke.sampleUsageHistory.v1';

export interface SampleRouterNavigationState {
  sampleId?: number;
  usageHistorySampleId?: number;
  sampleListLocationCell?: CaseLocationCell;
  caseManagerPendingCell?: CaseLocationCell;
  /** 使用履歴など「管理者は全件表示」画面から詳細へ移るとき、保管庫スコープを一時的に全件にする */
  preferAdminViewAll?: boolean;
  /** 詳細画面の「戻る」先（例: /samples/usage-records） */
  returnPath?: string;
}

export type SampleDetailSession = {
  sampleId: number;
  preferAdminViewAll?: boolean;
  returnPath?: string;
};

export function readSampleRouterState(location: Location): SampleRouterNavigationState {
  const state = location.state;
  if (state == null || typeof state !== 'object') return {};
  return state as SampleRouterNavigationState;
}

function parsePositiveSampleId(value: unknown): number | null {
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

function readJsonSession<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJsonSession(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode 等 */
  }
}

function clearSessionKey(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function writeSampleDetailSession(session: SampleDetailSession): void {
  writeJsonSession(SAMPLE_DETAIL_SESSION_KEY, session);
}

export function readSampleDetailSession(): SampleDetailSession | null {
  const raw = readJsonSession<SampleDetailSession>(SAMPLE_DETAIL_SESSION_KEY);
  if (!raw || parsePositiveSampleId(raw.sampleId) == null) return null;
  return {
    sampleId: raw.sampleId,
    ...(raw.preferAdminViewAll ? { preferAdminViewAll: true } : {}),
    ...(typeof raw.returnPath === 'string' && raw.returnPath.startsWith('/')
      ? { returnPath: raw.returnPath }
      : {}),
  };
}

export function clearSampleDetailSession(): void {
  clearSessionKey(SAMPLE_DETAIL_SESSION_KEY);
}

export function writeSampleUsageHistorySession(sampleId: number): void {
  writeJsonSession(SAMPLE_USAGE_HISTORY_SESSION_KEY, { sampleId });
}

export function readSampleUsageHistorySessionId(): number | null {
  const raw = readJsonSession<{ sampleId?: unknown }>(SAMPLE_USAGE_HISTORY_SESSION_KEY);
  return parsePositiveSampleId(raw?.sampleId);
}

export function clearSampleUsageHistorySession(): void {
  clearSessionKey(SAMPLE_USAGE_HISTORY_SESSION_KEY);
}

export function resolveSampleDetailId(location: Location): number | null {
  const fromState = parsePositiveSampleId(readSampleRouterState(location).sampleId);
  if (fromState != null) return fromState;
  if (location.pathname === SAMPLE_DETAIL_PATH) {
    return readSampleDetailSession()?.sampleId ?? null;
  }
  return null;
}

export function resolveUsageHistorySampleId(location: Location): number | null {
  const fromState = parsePositiveSampleId(readSampleRouterState(location).usageHistorySampleId);
  if (fromState != null) return fromState;
  if (location.pathname === SAMPLE_USAGE_HISTORY_PATH) {
    return readSampleUsageHistorySessionId();
  }
  return null;
}

export function resolveSampleListLocationCell(location: Location): CaseLocationCell | null {
  const cell = readSampleRouterState(location).sampleListLocationCell;
  if (cell == null || typeof cell !== 'object') return null;
  return cell;
}

export function resolveCaseManagerPendingCell(location: Location): CaseLocationCell | null {
  const cell = readSampleRouterState(location).caseManagerPendingCell;
  if (cell == null || typeof cell !== 'object') return null;
  return cell;
}

/** 一度読み取った state を履歴から除去（ID の残留防止） */
export function stripSampleRouterStateKeys(
  navigate: NavigateFunction,
  location: Location,
  keys: (keyof SampleRouterNavigationState)[]
): void {
  const current = readSampleRouterState(location);
  if (!keys.some((k) => current[k] !== undefined)) return;
  const next: SampleRouterNavigationState = { ...current };
  for (const key of keys) {
    delete next[key];
  }
  navigate(location.pathname + location.search, {
    replace: true,
    state: Object.keys(next).length > 0 ? next : null,
  });
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** 詳細画面の「戻る」ラベル（returnPath に応じて） */
export function resolveSampleDetailBackLabel(returnPath: string, t: TranslateFn): string {
  switch (returnPath) {
    case '/samples/usage-records':
      return t('usage_records.back_to_usage_records', { defaultValue: '履歴に戻る' });
    case '/samples/dashboard':
      return t('sample.back_to_dashboard');
    case '/samples/cases':
      return t('sample.back_to_case_manager');
    case '/reports':
      return t('sample.back_to_reports');
    case '/samples':
      return t('sample.back_to_list');
    default:
      return t('common.back');
  }
}

export function navigateToSampleDetail(
  navigate: NavigateFunction,
  sampleId: number,
  options?: { preferAdminViewAll?: boolean; returnPath?: string }
): void {
  const session: SampleDetailSession = {
    sampleId,
    ...(options?.preferAdminViewAll ? { preferAdminViewAll: true } : {}),
    ...(options?.returnPath ? { returnPath: options.returnPath } : {}),
  };
  writeSampleDetailSession(session);
  navigate(SAMPLE_DETAIL_PATH, {
    state: {
      sampleId,
      ...(options?.preferAdminViewAll ? { preferAdminViewAll: true } : {}),
      ...(options?.returnPath ? { returnPath: options.returnPath } : {}),
    } satisfies SampleRouterNavigationState,
  });
}

export function navigateToUsageHistory(navigate: NavigateFunction, sampleId: number): void {
  writeSampleUsageHistorySession(sampleId);
  navigate(SAMPLE_USAGE_HISTORY_PATH, {
    state: { usageHistorySampleId: sampleId } satisfies SampleRouterNavigationState,
  });
}

export function navigateToSampleListWithLocationCell(
  navigate: NavigateFunction,
  cell: CaseLocationCell
): void {
  navigate('/samples', {
    state: { sampleListLocationCell: cell } satisfies SampleRouterNavigationState,
  });
}

export function navigateToCaseManagerWithCell(
  navigate: NavigateFunction,
  cell: CaseLocationCell
): void {
  navigate('/samples/cases', {
    state: { caseManagerPendingCell: cell } satisfies SampleRouterNavigationState,
  });
}

/** 旧 /samples/:id 形式のパスセグメントか */
export function isLegacyNumericSamplePathSegment(segment: string | undefined): boolean {
  if (!segment) return false;
  if (segment === 'dashboard' || segment === 'cases' || segment === 'detail' || segment === 'usage-history') {
    return false;
  }
  const n = parseInt(segment, 10);
  return String(n) === segment && n >= 1;
}
